import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Searching from './pages/Searching';
import GameTable from './pages/GameTable';
import DealerGameList from './pages/DealerGameList';
import DealerGameControl from './pages/DealerGameControl';
import DealerGameControlRoute from './pages/DealerGameControlRoute';
import DealerHub from './pages/DealerHub';
import PwaInstallBanner from './components/PwaInstallBanner';
import './App.css';

function App() {
  // eslint-disable-next-line no-unused-vars
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [userId, setUserId] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  // Restore session from localStorage on mount
  useEffect(() => {
    const savedSession = localStorage.getItem('pokerSession');
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        setUsername(session.username || '');
        setDisplayName(session.displayName || '');
        setUserId(session.userId || null);
        setUserRoles(session.userRoles || []);
        setIsLoggedIn(true);
      } catch (e) {
        console.error('Error restoring session:', e);
        localStorage.removeItem('pokerSession');
      }
    }
  }, []);

  // Get user's location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          });
          setLocationError(null);
        },
        (error) => {
          console.error('Error getting location:', error);
          setLocationError('Unable to get location. Please enable location services.');
        },
        { enableHighAccuracy: true }
      );
    } else {
      setLocationError('Geolocation is not supported by this browser.');
    }
  }, []);

  const formatDisplayName = (firstName, lastName) => {
    if (firstName && lastName) {
      return `${firstName} ${lastName[0]}.`;
    }
    return '';
  };

  const handleLogin = (user, id, roles = [], firstName = '', lastName = '') => {
    setUsername(user);
    const formattedName = formatDisplayName(firstName, lastName);
    setDisplayName(formattedName || user);
    setUserId(id);
    setUserRoles(roles);
    setIsLoggedIn(true);

    // Save session to localStorage
    const session = {
      username: user,
      displayName: formattedName || user,
      userId: id,
      userRoles: roles,
    };
    localStorage.setItem('pokerSession', JSON.stringify(session));
  };

  const handleLogout = () => {
    setUsername('');
    setDisplayName('');
    setUserId(null);
    setUserRoles([]);
    setIsLoggedIn(false);
    setSelectedSeat(null);
    setSelectedGame(null);
    localStorage.removeItem('pokerSession');
  };

  // Check if user has the dealer role
  const isDealer = userRoles.includes('dealer');

  return (
    <Router>
      <div className="app-container">
        <PwaInstallBanner />
        <Routes>
          <Route
            path="/"
            element={
              !isLoggedIn ?
                <Login onLogin={handleLogin} /> :
                <Navigate to={isDealer ? "/dealer/hub" : "/searching"} />
            }
          />
          <Route
            path="/searching"
            element={
              isLoggedIn && !isDealer ?
                <Searching
                  username={displayName}
                  userId={userId}
                  userLocation={userLocation}
                  locationError={locationError}
                  onGameFound={setSelectedGame}
                  onLogout={handleLogout}
                /> :
                <Navigate to="/" />
            }
          />
          <Route
            path="/game"
            element={
              isLoggedIn && !isDealer ?
                <GameTable
                  username={displayName}
                  userId={userId}
                  userLocation={userLocation}
                  selectedSeat={selectedSeat}
                  setSelectedSeat={setSelectedSeat}
                  game={selectedGame}
                  onLogout={handleLogout}
                /> :
                <Navigate to="/" />
            }
          />
          <Route
            path="/dealer/games"
            element={
              isLoggedIn && isDealer ?
                <DealerGameList
                  username={displayName}
                  userId={userId}
                  userLocation={userLocation}
                  locationError={locationError}
                  onSelectGame={setSelectedGame}
                  onLogout={handleLogout}
                /> :
                <Navigate to="/" />
            }
          />
          <Route
            path="/dealer/hub"
            element={
              isLoggedIn && isDealer ?
                <DealerHub
                  username={displayName}
                  userId={userId}
                  userLocation={userLocation}
                  onLogout={handleLogout}
                /> :
                <Navigate to="/" />
            }
          />
          <Route
            path="/dealer/game/:gameId"
            element={
              isLoggedIn && isDealer ?
                <DealerGameControlRoute
                  username={displayName}
                  userId={userId}
                  userLocation={userLocation}
                  onLogout={handleLogout}
                /> :
                <Navigate to="/" />
            }
          />
          <Route
            path="/dealer/control"
            element={
              isLoggedIn && isDealer && selectedGame ?
                <DealerGameControl
                  username={displayName}
                  userId={userId}
                  userLocation={userLocation}
                  game={selectedGame}
                  onBackToGames={() => setSelectedGame(null)}
                  onLogout={handleLogout}
                /> :
                <Navigate to="/dealer/games" />
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
